/** Jigsaw Muse app-store screenshot layout tokens. */

import { androidLightTokens, androidTabCanvas } from "./androidJigsawTokens";

export const jigsawScreenshotAccent = "#3BA666";

export const appleDarkShellBg =
    "radial-gradient(ellipse 90% 70% at 12% 8%, rgba(245,158,11,0.14) 0%, transparent 52%), radial-gradient(ellipse 75% 55% at 88% 18%, rgba(20,184,166,0.22) 0%, transparent 48%), radial-gradient(ellipse 85% 65% at 72% 92%, rgba(6,95,70,0.55) 0%, transparent 58%), linear-gradient(152deg, #021812 0%, #0a3d38 24%, #064e3b 48%, #0f4c47 72%, #134e4a 100%)";

/**
 * Letterbox / shell fill for Google Ads (must match screenshot page shells).
 * @param {{ marketing?: string, layout?: string, theme?: string }} scene
 */
export function jigsawShellBgForScene(scene) {
    const marketing = scene?.marketing;
    const layout = scene?.layout;
    if (marketing === "hero") {
        return "radial-gradient(ellipse at 50% 0%, rgba(56,189,248,0.18) 0%, transparent 55%), linear-gradient(165deg, #020617 0%, #0c4a6e 38%, #0f172a 72%, #020617 100%)";
    }
    if (marketing === "colorBasket" || layout === "colorBasketDemo") {
        return "#0b1b3a";
    }
    if (marketing === "sorting") {
        return "linear-gradient(180deg, #fafafa 0%, #f4f4f5 55%, #e4e4e7 100%)";
    }
    if (marketing === "journey" || layout === "journeyMap") {
        return "radial-gradient(ellipse at 30% 20%, rgba(217,119,6,0.12) 0%, transparent 45%), linear-gradient(165deg, #1c1917 0%, #44403c 35%, #78716c 70%, #292524 100%)";
    }
    if (marketing === "giant") {
        return "#0a0a0a";
    }
    if (marketing === "daily") {
        return "radial-gradient(circle at 18% 22%, rgba(233,213,255,0.22) 0%, transparent 28%), radial-gradient(circle at 82% 70%, rgba(192,132,252,0.18) 0%, transparent 32%), linear-gradient(165deg, #2e1065 0%, #5b21b6 42%, #4c1d95 78%, #1e1b4b 100%)";
    }
    if (marketing === "vault" || layout === "vaultMenu") {
        return "linear-gradient(180deg, #f4f4f5 0%, #e4e4e7 48%, #d4d4d8 100%)";
    }
    if (marketing === "gallery" || layout === "twoColumnGrid") {
        return "#ffffff";
    }
    if (marketing === "tools") {
        return "radial-gradient(ellipse at 50% 15%, rgba(45,212,191,0.16) 0%, transparent 48%), linear-gradient(165deg, #042f2e 0%, #0f766e 28%, #115e59 55%, #134e4a 78%, #022c22 100%)";
    }
    // Apple in-app scenes: LIGHT theme canvases from the Android app (AppShell / PlayScreen).
    if (scene?.theme === "dark") {
        return appleDarkShellBg;
    }
    if (layout === "homeDashboard" || layout === "dailyMenu") {
        return androidTabCanvas.home;
    }
    if (layout === "archiveMenu" || layout === "chooseLevel") {
        return androidTabCanvas.library;
    }
    if (layout === "premiumPacks") {
        return androidTabCanvas.packs;
    }
    if (layout === "singleBoard" && !marketing) {
        return androidLightTokens.shellBg;
    }
    return "linear-gradient(180deg, #f8fafc 0%, #ffffff 38%, #eef2f7 100%)";
}

export const promoNavy = "#0f172a";
export const promoSubtitle = "#64748b";
export const promoPillText = "#334155";
export const promoSystemFont = "inherit";

export const phoneShellSx = {
    width: "100%",
    maxWidth: "100%",
    minHeight: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 38%, #eef2f7 100%)",
    color: promoNavy,
    pt: "max(env(safe-area-inset-top, 0px), 44px)",
    pb: "env(safe-area-inset-bottom, 0px)",
};

/** 01 — deep-sea midnight blue. */
export const heroShellSx = {
    ...phoneShellSx,
    background:
        "radial-gradient(ellipse at 50% 0%, rgba(56,189,248,0.18) 0%, transparent 55%), linear-gradient(165deg, #020617 0%, #0c4a6e 38%, #0f172a 72%, #020617 100%)",
    color: "#f8fafc",
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 16px)",
    overflow: "hidden",
};

/** 03 — crisp off-white (asymmetrical sorting). */
export const sortingShellSx = {
    ...phoneShellSx,
    background: "linear-gradient(180deg, #fafafa 0%, #f4f4f5 55%, #e4e4e7 100%)",
    color: promoNavy,
    pt: "max(env(safe-area-inset-top, 0px), 28px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 10px)",
    overflow: "hidden",
};

/** 08 — clean cream celebration. */
export const creamShellSx = {
    ...phoneShellSx,
    background:
        "radial-gradient(ellipse at 50% 12%, rgba(255,255,255,0.9) 0%, transparent 48%), linear-gradient(180deg, #faf7f2 0%, #f5f0e8 45%, #ebe4d8 100%)",
    color: promoNavy,
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 12px)",
    overflow: "hidden",
};

/** 03 — warm parchment map. */
export const parchmentShellSx = {
    ...phoneShellSx,
    background:
        "radial-gradient(ellipse at 30% 20%, rgba(217,119,6,0.12) 0%, transparent 45%), linear-gradient(165deg, #1c1917 0%, #44403c 35%, #78716c 70%, #292524 100%)",
    color: "#fafaf9",
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 16px)",
    overflow: "hidden",
};

/** 04 — full-bleed canvas (board fills frame; overlays sit on artwork). */
export const giantShellSx = {
    ...phoneShellSx,
    backgroundColor: "#0a0a0a",
    color: "#f8fafc",
    pt: 0,
    pb: 0,
    overflow: "hidden",
    position: "relative",
};

export const giantTopOverlaySx = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3,
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: 4,
    px: 2.5,
    textAlign: "center",
    background:
        "linear-gradient(180deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, transparent 100%)",
    pointerEvents: "none",
};

export const giantBottomOverlaySx = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
    display: "flex",
    justifyContent: "center",
    px: 2,
    pb: "max(env(safe-area-inset-bottom, 0px), 28px)",
    pt: 5,
    background:
        "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.22) 55%, transparent 100%)",
    pointerEvents: "none",
};

export const giantGlowPillSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    px: 2.5,
    py: 1.35,
    borderRadius: 999,
    bgcolor: "rgba(30, 41, 59, 0.72)",
    border: "1px solid rgba(255,255,255,0.22)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
    backdropFilter: "blur(10px)",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "0.98rem",
    whiteSpace: "nowrap",
};

/** 05 — deep purple particles. */
export const dailyShellSx = {
    ...phoneShellSx,
    background:
        "radial-gradient(circle at 18% 22%, rgba(233,213,255,0.22) 0%, transparent 28%), radial-gradient(circle at 82% 70%, rgba(192,132,252,0.18) 0%, transparent 32%), linear-gradient(165deg, #2e1065 0%, #5b21b6 42%, #4c1d95 78%, #1e1b4b 100%)",
    color: "#f8fafc",
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 16px)",
    overflow: "hidden",
    position: "relative",
};

/** 06 — immersive vault menu (light gray). */
export const vaultShellSx = {
    ...phoneShellSx,
    background: "linear-gradient(180deg, #f4f4f5 0%, #e4e4e7 48%, #d4d4d8 100%)",
    color: promoNavy,
    pt: "max(env(safe-area-inset-top, 0px), 32px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 16px)",
    overflow: "hidden",
    position: "relative",
    minHeight: "100%",
    height: "100%",
    maxHeight: "100%",
};

export const vaultPackCardSx = {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "#ffffff",
    boxShadow: "0 14px 36px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)",
    border: "1px solid rgba(148,163,184,0.22)",
};

/** 07 — midnight teal (legacy tools). */
export const toolsShellSx = {
    ...phoneShellSx,
    background:
        "radial-gradient(ellipse at 50% 15%, rgba(45,212,191,0.16) 0%, transparent 48%), linear-gradient(165deg, #042f2e 0%, #0f766e 28%, #115e59 55%, #134e4a 78%, #022c22 100%)",
    color: "#f8fafc",
    pt: "max(env(safe-area-inset-top, 0px), 36px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 16px)",
    overflow: "hidden",
};

/** 02 — Color Baskets (deep navy, glass mock). */
export const colorBasketShellSx = {
    ...phoneShellSx,
    background: "#0b1b3a",
    color: "#f8fafc",
    pt: "max(env(safe-area-inset-top, 0px), 22px)",
    pb: "max(env(safe-area-inset-bottom, 0px), 8px)",
    overflow: "hidden",
};

export const promoHeaderSx = {
    flexShrink: 0,
    px: 2,
    pt: 1.5,
    pb: 1.5,
    textAlign: "center",
};

export const promoHeaderLightSx = {
    ...promoHeaderSx,
    pt: 1,
    pb: 1.25,
};

/** Compact promo header for Facebook Ads 4:5 only. */
export const promoHeaderCompactSx = {
    ...promoHeaderSx,
    pt: 0.5,
    pb: 0.35,
};

export const promoHeaderCompactLightSx = {
    ...promoHeaderLightSx,
    pt: 0.35,
    pb: 0.25,
};

export const promoHeadlineStackSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.75,
    maxWidth: 340,
    mx: "auto",
};

export const promoHeadlineSx = {
    fontFamily: promoSystemFont,
    fontWeight: 700,
    fontSize: { xs: "1.85rem", sm: "2.1rem" },
    lineHeight: 1.25,
    letterSpacing: "-0.01em",
    color: promoNavy,
    m: 0,
};

export const promoHeadlineLightSx = {
    ...promoHeadlineSx,
    fontWeight: 800,
    fontSize: { xs: "1.7rem", sm: "1.95rem" },
    letterSpacing: "-0.02em",
    color: "#f8fafc",
    textShadow: "0 2px 18px rgba(2,6,23,0.45)",
};

export const promoHeadlineSubtitleSx = {
    fontFamily: promoSystemFont,
    fontWeight: 400,
    fontSize: { xs: "0.95rem", sm: "1.05rem" },
    lineHeight: 1.5,
    letterSpacing: "0.005em",
    color: promoSubtitle,
    m: 0,
};

export const promoHeadlineSubtitleLightSx = {
    ...promoHeadlineSubtitleSx,
    color: "rgba(226,232,240,0.88)",
};

export const galleryHeaderAmbientSx = {
    flexShrink: 0,
    px: 2,
    pt: "max(calc(env(safe-area-inset-top, 0px) + 8px), 40px)",
    pb: 1.5,
    textAlign: "center",
    background: "transparent",
    boxShadow: "none",
};

export const galleryHeadlineDarkSx = {
    fontFamily: promoSystemFont,
    fontWeight: 800,
    fontSize: { xs: "1.55rem", sm: "1.75rem" },
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    textTransform: "none",
    color: promoNavy,
    m: 0,
    textShadow: "none",
};

export const featurePillsRowSx = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
    mt: 1.75,
};

export const featurePillSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.35,
    py: 0.55,
    borderRadius: 999,
    bgcolor: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(148,163,184,0.32)",
    boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
    fontSize: "0.72rem",
    fontWeight: 600,
    fontFamily: promoSystemFont,
    color: promoPillText,
    whiteSpace: "nowrap",
};

export const boardRegionSx = {
    flexShrink: 0,
    width: "100%",
    boxSizing: "border-box",
};

export const boardRegionFramedSx = {
    ...boardRegionSx,
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: { xs: 1, sm: 1.5 },
    py: { xs: 0.75, sm: 1 },
};

export const boardFrameSx = {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: 0,
    boxShadow: "0 8px 32px rgba(15, 23, 42, 0.12)",
};

/** Phone chrome — full-width board (App Store / tablet screenshot capture). */
export const phoneDeviceFrameSx = {
    width: "100%",
    maxWidth: "100%",
    borderRadius: { xs: "28px", md: "36px" },
    p: { xs: "7px", md: "10px" },
    background:
        "linear-gradient(160deg, rgba(226,232,240,0.75) 0%, rgba(15,23,42,0.95) 48%, rgba(56,189,248,0.28) 100%)",
    boxShadow: "0 24px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(186,230,253,0.22)",
    boxSizing: "border-box",
};

export const phoneDeviceScreenSx = {
    borderRadius: "22px",
    overflow: "hidden",
    bgcolor: "#0f172a",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.35)",
};

export const galleryPhoneShellSx = {
    ...phoneShellSx,
    background: "#ffffff",
    pt: 0,
    pb: 0,
    minHeight: "100%",
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
};

export const galleryHeaderSx = {
    flexShrink: 0,
    px: 2,
    pt: "max(calc(env(safe-area-inset-top, 0px) + 12px), 56px)",
    pb: 2,
    textAlign: "center",
    background: "linear-gradient(135deg, #0f172a 0%, #134e4a 38%, #3BA666 92%)",
    boxShadow: "0 6px 28px rgba(15, 23, 42, 0.14)",
};

export const galleryHeadlineSx = {
    fontFamily: promoSystemFont,
    fontWeight: 800,
    fontSize: { xs: "1.75rem", sm: "2rem" },
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    color: "#ffffff",
    m: 0,
    textShadow: "0 2px 16px rgba(0, 0, 0, 0.22)",
};

export const galleryGridRegionFramedSx = {
    flex: 1,
    minHeight: 0,
    width: "100%",
    mx: 1.5,
    mb: 1.5,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    bgcolor: "#ffffff",
    overflow: "hidden",
    borderRadius: "22px",
    boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
    border: "1px solid rgba(148,163,184,0.28)",
};

export const metaRegionOverlaySx = {
    flex: "0 0 auto",
    minHeight: 88,
    mt: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: 2,
    py: 2,
    background: "transparent",
};

/** Compact meta strip for Facebook Ads 4:5 only — no gap under the board. */
export const metaRegionCompactSx = {
    ...metaRegionOverlaySx,
    minHeight: 56,
    mt: 0,
    py: 0.75,
};

export const metaPillsRowSx = {
    width: "100%",
    maxWidth: 360,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.25,
};

export const metaPillSx = {
    flex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    px: 1.75,
    py: 1.35,
    borderRadius: 999,
    bgcolor: "rgba(30,41,59,0.82)",
    border: "1px solid rgba(148,163,184,0.28)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
    backdropFilter: "blur(8px)",
    color: "#f8fafc",
    fontWeight: 700,
    fontSize: "0.95rem",
    whiteSpace: "nowrap",
};

export const metaPillHardSx = {
    ...metaPillSx,
    "& .meta-pill-shield": {
        color: "#f87171",
    },
};

export const utilityBarSx = {
    flexShrink: 0,
    mx: 1.5,
    mb: "max(env(safe-area-inset-bottom, 0px), 14px)",
    mt: 1,
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-around",
    gap: 0.5,
    px: 1.25,
    py: 1.25,
    borderRadius: 3,
    bgcolor: "rgba(15,23,42,0.92)",
    border: "1px solid rgba(148,163,184,0.28)",
    boxShadow: "0 10px 28px rgba(15,23,42,0.22)",
};

export const utilityBarLightSx = {
    ...utilityBarSx,
    bgcolor: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(148,163,184,0.35)",
    boxShadow: "0 8px 22px rgba(15,23,42,0.1)",
};

export const utilityItemSx = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.45,
    minWidth: 0,
    color: "#f8fafc",
};

export const utilityItemDarkTextSx = {
    ...utilityItemSx,
    color: promoNavy,
};

export const panelCardSx = {
    flexShrink: 0,
    mx: 1.5,
    mb: "max(env(safe-area-inset-bottom, 0px), 14px)",
    mt: 1,
    borderRadius: 2.5,
    p: 1.75,
    bgcolor: "rgba(15,23,42,0.88)",
    border: "1px solid rgba(148,163,184,0.28)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
    color: "#f8fafc",
};

export const panelCardLightSx = {
    ...panelCardSx,
    bgcolor: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(148,163,184,0.35)",
    boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
    color: promoNavy,
};

export const ctaButtonSx = {
    mt: 1.25,
    width: "100%",
    py: 1.25,
    borderRadius: 999,
    bgcolor: jigsawScreenshotAccent,
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    textAlign: "center",
    boxShadow: "0 6px 18px rgba(59,166,102,0.35)",
};

export const glowingBoardWrapSx = {
    borderRadius: 2,
    p: "3px",
    background:
        "linear-gradient(135deg, rgba(233,213,255,0.95), rgba(167,139,250,0.7), rgba(192,132,252,0.9))",
    boxShadow: "0 0 28px rgba(167,139,250,0.45), 0 12px 32px rgba(0,0,0,0.28)",
};

export const vaultRibbonSx = {
    flexShrink: 0,
    mx: 2,
    mb: "max(env(safe-area-inset-bottom, 0px), 14px)",
    mt: 0.5,
    py: 1,
    px: 1.5,
    textAlign: "center",
    borderRadius: 999,
    bgcolor: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(148,163,184,0.35)",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: promoPillText,
};
